import {ReactNode, Suspense, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {number, object, string} from "yup";

import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {TextareaField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";

import {IApplication} from "../../../../application/types/IApplication";
import {IApplicationFormValues} from "../../../../application/types/IApplicationFormValues";
import {validationMessages} from "../../../../form/utils/validation_messages";
import {hitGTMEcommerceAddToCart} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../../../property/types/IPropertyDetailApplication";
import {useAppSelector} from "../../../../store/hooks";
import {gtmCheckout} from "../../../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../../../tracking/google_tag_manager/gtm_product_add";
import {isValidationError} from "../../../../utils/isValidationError";
import {useSendMainLead} from "../../../hooks/use_send_main_lead";
import {FormSubmitFooter} from "../../FormSubmitFooter";
import {UserContactDataForm} from "../../UserContactDataForm";

const formSchema = object().shape({
    email: string().email().required(validationMessages.email),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    }),
    message: string().required(validationMessages.required).max(500, validationMessages.maxLength)
});

interface IDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
    message: string;
}

interface IProps {
    handleModalClose: (forceClose?: boolean) => void;
    onSuccessfulSubmit: (
        sentApplication: IApplication | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    storeHash?: string;
    initialLeadFormValues: IApplicationFormValues | null;
    offerId: number | null;
    propertyId: number | null;
    customHeader: ReactNode;
    defaultMessage: string;
}

export const TopicForm = (props: IProps) => {
    const [isSending, setIsSending] = useState(false);
    const {handleModalClose, onSuccessfulSubmit, storeHash, initialLeadFormValues, offerId, customHeader, defaultMessage} = props;

    const leadModalState = useAppSelector((state) => state.mainLead);
    const applicationData = useAppSelector((state) => state.application[storeHash ?? ""]);
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);

    const wasInteractionHitRef = useRef(false);

    const defaultValues: IDefaultValues = {
        name: initialLeadFormValues?.name || "",
        email: initialLeadFormValues?.email || "",
        phone: initialLeadFormValues?.phone || {code: 48, number: ""},
        marketing_email: initialLeadFormValues?.marketing_email || false,
        marketing_sms: initialLeadFormValues?.marketing_sms || false,
        message: defaultMessage
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(formSchema)
    });

    const {handleSubmit, setError} = formMethods;

    const sendLead = useSendMainLead();

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && mainLeadType && leadModalState.sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            // TODO: probably `context_trigger` need to be changed to ApplicationSourceSection if A/B test (OngoingABTests.LEAD_TOPIC_FORM) will be approved (BE required)
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, "context_trigger", formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (mainLeadType && leadModalState.sourceSection && applicationData) {
            gtmProductAdd(applicationData, viewType, mainLeadType);
            // TODO: probably `context_trigger` need to be changed to ApplicationSourceSection if A/B test (OngoingABTests.LEAD_TOPIC_FORM) will be approved (BE required)
            hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, "context_trigger");

            if (initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number) {
                // form will be filled with data automatically
                onInteraction("autofill");
            }
        }
    }, [mainLeadType, leadModalState.sourceSection, applicationData]);

    const onSubmit = async (values: IDefaultValues) => {
        const formValues: IApplicationFormValues = {
            name: values.name,
            email: values.email,
            phone: values.phone,
            marketing_email: values.marketing_email,
            marketing_sms: values.marketing_sms,
            message: values.message
        };

        setIsSending(true);

        try {
            const application = (await sendLead(formValues, handleModalClose, {
                analyticsParams: {customSourceSection: "context_trigger"}
            })) as unknown as IApplication;

            await onSuccessfulSubmit(application, applicationData?.offer?.detail, applicationData?.property?.detail);
        } catch (error) {
            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    setError(fieldName as keyof IDefaultValues, {message: errorMessage.join(", ")});
                }
            }
        } finally {
            setIsSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)} onClick={() => onInteraction()}>
                    {customHeader}

                    <UserContactDataForm />

                    <Text variant="body_copy_2" mb={1}>
                        Wiadomość:
                    </Text>

                    <TextareaField className={css({mb: 3})} name="message" placeholder="Wpisz wiadomość do dewelopera." rows={6} />

                    <FormSubmitFooter isSending={isSending} buttonText="Wyślij" />
                </form>
            </FormProvider>
        </Suspense>
    );
};
import {ReactNode, Suspense, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {number, object, string} from "yup";

import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {TextareaField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";

import {IApplication} from "../../../../application/types/IApplication";
import {IApplicationFormValues} from "../../../../application/types/IApplicationFormValues";
import {validationMessages} from "../../../../form/utils/validation_messages";
import {hitGTMEcommerceAddToCart} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../../../property/types/IPropertyDetailApplication";
import {useAppSelector} from "../../../../store/hooks";
import {gtmCheckout} from "../../../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../../../tracking/google_tag_manager/gtm_product_add";
import {isValidationError} from "../../../../utils/isValidationError";
import {useSendMainLead} from "../../../hooks/use_send_main_lead";
import {FormSubmitFooter} from "../../FormSubmitFooter";
import {UserContactDataForm} from "../../UserContactDataForm";

const formSchema = object().shape({
    email: string().email().required(validationMessages.email),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    }),
    message: string().required(validationMessages.required).max(500, validationMessages.maxLength)
});

interface IDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
    message: string;
}

interface IProps {
    handleModalClose: (forceClose?: boolean) => void;
    onSuccessfulSubmit: (
        sentApplication: IApplication | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    storeHash?: string;
    initialLeadFormValues: IApplicationFormValues | null;
    offerId: number | null;
    propertyId: number | null;
    customHeader: ReactNode;
    defaultMessage: string;
}

export const TopicForm = (props: IProps) => {
    const [isSending, setIsSending] = useState(false);
    const {handleModalClose, onSuccessfulSubmit, storeHash, initialLeadFormValues, offerId, customHeader, defaultMessage} = props;

    const leadModalState = useAppSelector((state) => state.mainLead);
    const applicationData = useAppSelector((state) => state.application[storeHash ?? ""]);
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);

    const wasInteractionHitRef = useRef(false);

    const defaultValues: IDefaultValues = {
        name: initialLeadFormValues?.name || "",
        email: initialLeadFormValues?.email || "",
        phone: initialLeadFormValues?.phone || {code: 48, number: ""},
        marketing_email: initialLeadFormValues?.marketing_email || false,
        marketing_sms: initialLeadFormValues?.marketing_sms || false,
        message: defaultMessage
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(formSchema)
    });

    const {handleSubmit, setError} = formMethods;

    const sendLead = useSendMainLead();

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && mainLeadType && leadModalState.sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            // TODO: probably `context_trigger` need to be changed to ApplicationSourceSection if A/B test (OngoingABTests.LEAD_TOPIC_FORM) will be approved (BE required)
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, "context_trigger", formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (mainLeadType && leadModalState.sourceSection && applicationData) {
            gtmProductAdd(applicationData, viewType, mainLeadType);
            // TODO: probably `context_trigger` need to be changed to ApplicationSourceSection if A/B test (OngoingABTests.LEAD_TOPIC_FORM) will be approved (BE required)
            hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, "context_trigger");

            if (initialLeadFormValues?.name || initialLeadFormValues?.email || initialLeadFormValues?.phone?.number) {
                // form will be filled with data automatically
                onInteraction("autofill");
            }
        }
    }, [mainLeadType, leadModalState.sourceSection, applicationData]);

    const onSubmit = async (values: IDefaultValues) => {
        const formValues: IApplicationFormValues = {
            name: values.name,
            email: values.email,
            phone: values.phone,
            marketing_email: values.marketing_email,
            marketing_sms: values.marketing_sms,
            message: values.message
        };

        setIsSending(true);

        try {
            const application = (await sendLead(formValues, handleModalClose, {
                analyticsParams: {customSourceSection: "context_trigger"}
            })) as unknown as IApplication;

            await onSuccessfulSubmit(application, applicationData?.offer?.detail, applicationData?.property?.detail);
        } catch (error) {
            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    setError(fieldName as keyof IDefaultValues, {message: errorMessage.join(", ")});
                }
            }
        } finally {
            setIsSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)} onClick={() => onInteraction()}>
                    {customHeader}

                    <UserContactDataForm />

                    <Text variant="body_copy_2" mb={1}>
                        Wiadomość:
                    </Text>

                    <TextareaField className={css({mb: 3})} name="message" placeholder="Wpisz wiadomość do dewelopera." rows={6} />

                    <FormSubmitFooter isSending={isSending} buttonText="Wyślij" />
                </form>
            </FormProvider>
        </Suspense>
    );
};
